/**
 * A yes / no question with no default.
 *
 * The impartiality and consultancy questions are `boolean not null` in the data
 * dictionary, and *unanswered* is not the same fact as *No*. A pre-selected radio would
 * record an answer the applicant never gave, so nothing is checked until they choose —
 * and the server blocks the step while the value is null.
 */

export interface BooleanChoiceProps {
  id: string;
  label: string;
  value: boolean | null;
  onChange: (value: boolean) => void;
  hint?: string;
  error?: string;
  required?: boolean;
  /** Render No before Yes, as screen 04 does for the consultancy question. */
  negativeFirst?: boolean;
}

export function BooleanChoice({
  id,
  label,
  value,
  onChange,
  hint,
  error,
  required = false,
  negativeFirst = false,
}: BooleanChoiceProps) {
  const choices: readonly { key: string; label: string; choice: boolean }[] = negativeFirst
    ? [
        { key: "no", label: "No", choice: false },
        { key: "yes", label: "Yes", choice: true },
      ]
    : [
        { key: "yes", label: "Yes", choice: true },
        { key: "no", label: "No", choice: false },
      ];

  const describedBy = [hint ? `${id}-hint` : null, error ? `${id}-error` : null]
    .filter(Boolean)
    .join(" ");

  return (
    <fieldset className="field" aria-describedby={describedBy || undefined}>
      <legend className="f">
        {label} {required ? <b>*</b> : null}
        {hint ? <i id={`${id}-hint`}> {hint}</i> : null}
      </legend>
      <div className="radios">
        {choices.map((option) => (
          <label key={option.key} htmlFor={`${id}-${option.key}`}>
            <input
              type="radio"
              id={`${id}-${option.key}`}
              name={id}
              checked={value === option.choice}
              onChange={() => onChange(option.choice)}
            />
            {option.label}
          </label>
        ))}
      </div>
      {error ? (
        <p className="err" id={`${id}-error`} role="alert">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}
